import type { ComponentProps, ReactNode } from "react";
import { Lucide } from "@react-native-vector-icons/lucide";
import { Pressable, StyleSheet, View } from "react-native";

import {
  radii,
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { ButtonContext } from "./button-context";
import type { ButtonVariant } from "./types";

function iconColorForVariant(variant: ButtonVariant, colors: ThemeColors) {
  if (variant === "primary") return colors.primaryForeground;
  if (variant === "link") return colors.primary;
  return variant === "danger" ? colors.danger : colors.heading;
}

export function Button({
  children,
  icon,
  iconName,
  iconPosition = "left",
  variant = "primary",
  compact = false,
  disabled,
  align = "stretch",
  ...props
}: Omit<ComponentProps<typeof Pressable>, "style"> & {
  children?: ReactNode;
  icon?: ReactNode;
  iconName?: ComponentProps<typeof Lucide>["name"];
  iconPosition?: "left" | "right";
  variant?: ButtonVariant;
  compact?: boolean;
  align?: "stretch" | "start" | "center";
}) {
  const { colors } = useTheme();
  const { stylesheet, variantStyles, hoverStyles } =
    useThemedStyles(makeStyles);
  const iconColor = iconColorForVariant(variant, colors);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: Boolean(disabled) }}
      disabled={disabled}
      style={({ hovered, pressed }) => [
        stylesheet.button,
        compact ? stylesheet.compact : null,
        variantStyles[variant],
        hovered || pressed ? hoverStyles[variant] : null,
        align === "start" ? stylesheet.alignStart : null,
        align === "center" ? stylesheet.alignCenter : null,
        disabled ? stylesheet.disabled : null,
      ]}
      {...props}
    >
      <ButtonContext.Provider value={variant}>
        <View
          style={[
            stylesheet.content,
            variant === "text" ? null : stylesheet.contentCentered,
          ]}
        >
          {iconPosition === "left" ? icon : null}
          {iconPosition === "left" && iconName ? (
            <Lucide name={iconName} size={16} color={iconColor} />
          ) : null}
          {children}
          {iconPosition === "right" && iconName ? (
            <Lucide name={iconName} size={16} color={iconColor} />
          ) : null}
          {iconPosition === "right" ? icon : null}
        </View>
      </ButtonContext.Provider>
    </Pressable>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    button: {
      minHeight: 44,
      justifyContent: "center",
      paddingHorizontal: spacing.l,
      paddingVertical: spacing.s,
      borderRadius: radii.control,
      borderWidth: 1,
      borderColor: "transparent",
    },
    compact: { minHeight: 36, paddingVertical: spacing.xs },
    primary: { backgroundColor: colors.primary, borderColor: colors.primary },
    secondary: {
      backgroundColor: colors.surface,
      borderColor: colors.border,
    },
    outline: {
      borderColor: colors.border,
      backgroundColor: colors.elevated,
    },
    danger: {
      borderColor: colors.danger,
      backgroundColor: colors.elevated,
    },
    text: { backgroundColor: "transparent" },
    link: { backgroundColor: "transparent" },
    primaryHover: {
      backgroundColor: colors.primaryHover,
      borderColor: colors.primaryHover,
    },
    neutralHover: { backgroundColor: colors.surface },
    dangerHover: { backgroundColor: colors.dangerSurface },
    content: { flexDirection: "row", alignItems: "center", gap: spacing.s },
    contentCentered: { justifyContent: "center" },
    alignStart: { alignSelf: "flex-start" },
    alignCenter: { alignItems: "center" },
    disabled: { opacity: 0.5 },
  });
  return {
    stylesheet,
    variantStyles: {
      primary: stylesheet.primary,
      secondary: stylesheet.secondary,
      outline: stylesheet.outline,
      danger: stylesheet.danger,
      text: stylesheet.text,
      link: stylesheet.link,
    },
    hoverStyles: {
      primary: stylesheet.primaryHover,
      secondary: stylesheet.neutralHover,
      outline: stylesheet.neutralHover,
      danger: stylesheet.dangerHover,
      text: stylesheet.neutralHover,
      link: stylesheet.neutralHover,
    },
  };
};
